import React, { useEffect, useState } from 'react';
import { Author } from '../../below-article/types/Author';
import AuthorAndTopicBox from '../../below-article/AuthorAndTopicBox';
import { useTranslations } from '../../localization/useTranslations';
import Button from '../../components/buttons/SimpleButton/Button';

interface AuthorListProps {
  selectedRole: string;
}

const AuthorList: React.FC<AuthorListProps> = ({ selectedRole }) => {
  const [coauthors, setCoauthors] = useState<Author[]>([]);
  const [visibleCount, setVisibleCount] = useState(15);
  const [, setSubscribedAuthors] = useState<number[]>([]);
  const [, setAvailableAuthors] = useState<number[]>([]);
  const [, setIsLoggedIn] = useState(false);
  const translations = useTranslations(); // Hook for fetching translations

  useEffect(() => {
    // Load coauthors from global window object if available
    if (window.Coauthors) {
      setCoauthors(window.Coauthors);
    }

    // Function to fetch subscribed authors from global window object
    const fetchSubscribedAuthors = () => {
      try {
        const userSubscriptions = window.crm_user?.subscriptions || [];
        const authorSubscriptions = userSubscriptions.find((sub) => sub.code === 'authors')?.variants || [];

        setSubscribedAuthors(authorSubscriptions.map(Number));
      } catch (error) {
        console.error('Failed to fetch subscribed authors', error);
      }
    };

    // Function to fetch available authors from newsletter service
    const fetchAvailableAuthors = async () => {
      const response = window.NewsletterLists;
      const ids: number[] = [];

      if (response.status !== 'ok') {
        response.lists.forEach((list) => {
          Object.values(list.variants).forEach((variant) => {
            ids.push(Number(variant.code));
          });
        });

        setAvailableAuthors(ids);
      } else {
        console.error('Failed to fetch available authors');
      }
    };

    // Fetch subscribed and available authors
    fetchSubscribedAuthors();
    fetchAvailableAuthors();

    // Check if user is logged in
    const user = window.crm_user;
    if (user) {
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    // Reset visible count when selectedRole changes
    setVisibleCount(15);
  }, [selectedRole]);

  // Function to load more authors
  const loadMore = () => {
    setVisibleCount((prevCount) => prevCount + 15);
  };

  // Filter coauthors based on selected role
  const filteredCoauthors = coauthors.filter((coauthor) => {
    return (
      selectedRole === 'all' ||
      (Array.isArray(coauthor.role) ? coauthor.role.includes(selectedRole) : coauthor.role === selectedRole)
    );
  });

  return (
    <div className="authors-content">
      <div className="container">
        <div className="authors__grid">
          {/* Render the AuthorAndTopicBox component with filtered coauthors */}
          <AuthorAndTopicBox
            cardsType="author"
            layout="article-top"
            selectedRole={selectedRole}
            authors={filteredCoauthors.slice(0, visibleCount)}
          />
        </div>
        {/* Render the Load More button if there are more authors to show */}
        {visibleCount < filteredCoauthors.length && (
          <div className="authors-content__button-wrapper">
            <Button
              className=""
              label={translations?.general?.loadMore || 'Load More'}
              type="primary"
              size="medium"
              onClick={loadMore}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorList;
